import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { SignalDetailsContext } from "./SignalDetails.context";
import {
  ApiGeneralDataResponse,
  GetExchangeSignalByIdRequest,
  GetSignalByIdRequest,
  PrecisionType,
  PriceGraphData,
  QueryStatus,
  RawLineChartDataPoint,
  SignalGraphRequest,
  TimeRange,
} from "@shared/types";
import {
  SIGNAL_EXTRA_DETAILS_TABS,
  TIME_RANGE_START,
} from "@shared/common/constants";
import { ValueOf } from "@shared/types/utils";
import {
  getFormattedTargets,
  getTargetsAnnotations,
} from "@shared/features/signals/common/utils";
import {
  getActivitiesAnnotations,
  getPriceGraphDataWithActivities,
} from "@shared/features/trades/common/utils";
import { useTheme } from "styled-components";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { sortByStringProperty } from "@shared/utils/general";
import { useIsPersonalGroup } from "@shared/features/signals/common/hooks";

interface SignalDetailsProviderProps {
  signalId?: number;
  forAdmin?: boolean;
  tab?: ValueOf<typeof SIGNAL_EXTRA_DETAILS_TABS>;
  isEditUrl: boolean;
}

const SignalDetailsProvider: FC<
  PropsWithChildren<SignalDetailsProviderProps>
> = ({ signalId, forAdmin = false, tab, isEditUrl, children }) => {
  const theme = useTheme();
  const [selectedExchange, setSelectedExchange] = useState<string>();
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<TimeRange>(TIME_RANGE_START);

  const baseArgs: GetSignalByIdRequest | undefined = signalId
    ? {
        signal_id: signalId,
        for_admin: forAdmin,
      }
    : undefined;

  const getExchangeSignalIdsQuery =
    sharedLib.sharedApi.signals.useGetExchangeSignalIdsQuery(
      baseArgs ?? skipToken
    );
  const configurationQuery =
    sharedLib.sharedApi.signals.useGetSignalExtraInfoConfigurationQuery(
      baseArgs ?? skipToken
    );
  const getMostFulfilledSignalForGroupQuery =
    sharedLib.sharedApi.signals.useGetMostFulfilledSignalForGroupQuery(
      baseArgs ?? skipToken
    );

  const { data: signalExchangesMap } = getExchangeSignalIdsQuery;
  const signalExchanges = useMemo(
    () =>
      (signalExchangesMap ? Object.keys(signalExchangesMap) : []).sort(
        sortByStringProperty
      ),
    [signalExchangesMap]
  );

  useEffect(() => {
    if (!selectedExchange && signalExchanges?.length) {
      setSelectedExchange(signalExchanges[0]);
    } else if (
      selectedExchange &&
      signalExchanges?.length &&
      !signalExchanges?.includes(selectedExchange)
    ) {
      setSelectedExchange(signalExchanges[0]);
    }
  }, [selectedExchange, signalExchanges]);

  const exchangeSignalId = useMemo(() => {
    let exchangeSignalId;
    if (signalExchanges.length) {
      if (selectedExchange)
        exchangeSignalId = signalExchangesMap?.[selectedExchange];
      else exchangeSignalId = signalExchangesMap?.[signalExchanges[0]];
    }
    return exchangeSignalId || 0;
  }, [selectedExchange, signalExchangesMap]);

  const graphArgs: SignalGraphRequest | undefined = useMemo(
    () =>
      exchangeSignalId
        ? {
            exchange_signal_id: exchangeSignalId,
            time_range: selectedTimeRange,
            for_admin: false,
          }
        : undefined,
    [exchangeSignalId, selectedTimeRange]
  );

  const exchangeSignalArgs: GetExchangeSignalByIdRequest | undefined = useMemo(
    () =>
      exchangeSignalId
        ? {
            ignore_status: true,
            exchange_signal_id: exchangeSignalId,
            for_admin: forAdmin,
          }
        : undefined,
    [selectedExchange, exchangeSignalId, forAdmin]
  );

  const generalDataQuery =
    sharedLib.sharedApi.signals.useGetSignalExtraInfoGeneralQuery(
      exchangeSignalArgs ?? skipToken
    );

  const generalData = generalDataQuery.currentData;
  const isPersonalGroupSignal = useIsPersonalGroup(
    generalData?.sub_group_id,
    forAdmin
  );
  const showEditSignalActions =
    generalData?.is_open && (forAdmin || isPersonalGroupSignal);
  const isEdit = isEditUrl && (forAdmin || isPersonalGroupSignal);

  const getSignalExtraInfoTargetsQuery =
    sharedLib.sharedApi.signals.useGetSignalExtraInfoTargetsQuery(
      exchangeSignalArgs ?? skipToken,
      {
        refetchOnMountOrArgChange: isEdit,
      }
    );

  const getPriceGraphDataQuery =
    sharedLib.sharedApi.common.useGetPriceGraphDataQuery(
      graphArgs ?? skipToken
    ) as QueryStatus<
      ApiGeneralDataResponse<PriceGraphData>,
      SignalGraphRequest
    >;

  const getSignalExtraInfoActivitiesQuery =
    sharedLib.sharedApi.signals.useGetSignalExtraInfoActivitiesQuery(
      exchangeSignalArgs ?? skipToken
    );

  useEffect(() => {
    if (isEdit) {
      if (tab === SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION)
        configurationQuery.refetch();
      else if (tab === SIGNAL_EXTRA_DETAILS_TABS.TARGETS) {
        getMostFulfilledSignalForGroupQuery.refetch();
      }
    }
  }, [isEdit]);

  const targetsData = getSignalExtraInfoTargetsQuery?.data?.data;
  const rawPriceGraphData = getPriceGraphDataQuery?.data?.data.graph_data;
  const activities = getSignalExtraInfoActivitiesQuery?.data;
  const symbol = targetsData?.symbol;
  const priceCoin = targetsData?.price_coin;
  const precisionConfig = getSignalExtraInfoTargetsQuery?.data?.precision;
  const currentPrice = targetsData?.current_price;
  const { getPrecision } = usePrecisions();
  const pricePrecision = getPrecision(
    PrecisionType.PRICE,
    symbol,
    selectedExchange
  );

  const priceGraphData = useMemo(() => {
    if (rawPriceGraphData) {
      const parsedData: RawLineChartDataPoint[] =
        rawPriceGraphData?.[selectedTimeRange]?.map((point) => [
          point[0],
          point[2],
        ]) || [];
      return getPriceGraphDataWithActivities(
        parsedData,
        activities,
        precisionConfig
      );
    }
    return [];
  }, [rawPriceGraphData, selectedTimeRange, precisionConfig]);

  const activitiesAnnotations = useMemo(() => {
    return getActivitiesAnnotations(
      getSignalExtraInfoActivitiesQuery?.data || [],
      theme
    );
  }, [getSignalExtraInfoActivitiesQuery?.data]);

  const targets = useMemo(() => {
    if (targetsData) {
      return getFormattedTargets(
        targetsData,
        targetsData?.reverse,
        targetsData?.breakout
      );
    }
  }, [targetsData]);

  const targetsAnnotations = useMemo(() => {
    if (!targets || !precisionConfig) return [];
    return getTargetsAnnotations(
      theme,
      targets.entryTargets,
      targets.takeProfitTargets,
      targets.stopTargets,
      precisionConfig,
      isEdit,
      currentPrice
    );
  }, [theme, targets, precisionConfig, isEdit, currentPrice]);

  return (
    <SignalDetailsContext.Provider
      value={{
        isEdit,
        signalId,
        baseArgs,
        exchangeSignalArgs,
        selectedTimeRange,
        setSelectedTimeRange,
        generalDataQuery,
        configurationQuery,
        getMostFulfilledSignalForGroupQuery,
        getSignalExtraInfoTargetsQuery,
        getPriceGraphDataQuery,
        getSignalExtraInfoActivitiesQuery,
        getExchangeSignalIdsQuery,
        selectedExchange,
        setSelectedExchange,
        signalExchanges,
        forAdmin,
        priceCoin,
        precisionConfig,
        priceGraphData,
        currentPrice,
        activitiesAnnotations: priceGraphData.length
          ? activitiesAnnotations
          : [],
        targetsAnnotations: priceGraphData.length ? targetsAnnotations : [],
        targets,
        tab,
        symbol,
        pricePrecision,
        showEditSignalActions,
      }}
    >
      {children}
    </SignalDetailsContext.Provider>
  );
};

export default SignalDetailsProvider;
